/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Remember what the teacher already did about each alert, so it stops re-asking.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; shared transient reset registry
 * @forbidden server entities; roles; permissions; tokens; localStorage; API client
 * @public-api live module
 * @side-effects in-memory UI updates only
 * @tests modules/live/store/seat-alerts.test.ts
 */

import { create } from 'zustand';
import { registerWorkspaceTransientReset } from '../../../shared/lib/transient-reset';

/**
 * `pinned` keeps a seat at the top; `snoozed` hides it until the clock runs out; `ignored` drops
 * it for this session; `ack` means seen and handled. A teacher who has already acted on something
 * should not be shown it again as if nothing happened.
 */
export type SeatAlertState = 'open' | 'ack' | 'snoozed' | 'pinned' | 'ignored';

export interface SeatAlert {
  readonly state: SeatAlertState;
  /** Minute-of-session the snooze lifts; the caller compares it against its own clock. */
  readonly until?: number;
}

export const SNOOZE_MINUTES = 10;

function key(classIndex: number, student: string): string {
  return `${classIndex}|${student}`;
}

interface SeatAlertsState {
  alerts: Readonly<Record<string, SeatAlert>>;
  setAlert(classIndex: number, student: string, alert: SeatAlert): void;
  reopen(classIndex: number, student: string): void;
  clear(): void;
}

export const useSeatAlerts = create<SeatAlertsState>((set) => ({
  alerts: {},
  setAlert: (classIndex, student, alert) =>
    set((state) => ({ alerts: { ...state.alerts, [key(classIndex, student)]: alert } })),
  reopen: (classIndex, student) =>
    set((state) => {
      const next = { ...state.alerts };
      delete next[key(classIndex, student)];
      return { alerts: next };
    }),
  clear: () => set({ alerts: {} }),
}));

export function selectAlert(
  alerts: Readonly<Record<string, SeatAlert>>,
  classIndex: number,
  student: string,
): SeatAlert {
  return alerts[key(classIndex, student)] ?? { state: 'open' };
}

/** A snoozed alert comes back on its own; everything else stays where the teacher put it. */
export function isMuted(alert: SeatAlert, elapsedMinutes: number): boolean {
  if (alert.state === 'ignored' || alert.state === 'ack') return true;
  if (alert.state === 'snoozed') return elapsedMinutes < (alert.until ?? 0);
  return false;
}

registerWorkspaceTransientReset('seat-alerts', () => useSeatAlerts.getState().clear());
